/*
 * Main component for variation analysis charts
 * It contains the carrousels for ECL departure, ECL arrival and ECL variation
 * It then displays several charts and tables with the data received from the API.
 */

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import { Row, Col, Carousel } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import {
  getClientPalette,
  defaultPalette,
} from "../../../../../config/clientPalettes";
import { useGroupedItems } from "hooks/useGroupedItems";

import BreakdownByBucket from "./BreakdownByBucket";
import BreakdownBySector from "./BreakdownBySector";
import BreakdownByRating from "./BreakdownByRating";
import CountriesWithBucket from "./CountriesWithBucket";
import WaterfallEffects from "./WaterfallEffects";
import MarginalCumulativeEffects from "./MarginalCumulativeEffects";
import TopRatingEffects from "./TopRatingEffects";
import TopPdEffects from "./TopPdEffects";
import TopLgdEffects from "./TopLgdEffects";
import TopTimeEffects from "./TopTimeEffects";
import TopPurchaseEffects from "./TopPurchaseEffects";
import TopIssuerEffects from "./TopIssuerEffects";
import RatingEffectDistribution from "./RatingEffectDistribution";
import BucketEffectDistribution from "./BucketEffectDistribution";
import TimeEffectDistribution from "./TimeEffectDistribution";
import EmptyCardVariation from "components/common/pyramid/EmptyCardVariation";

/**
 * CarouselSection Component
 *
 * Displays a carousel with ECL departure and arrival data,
 * along with a static card showing ECL variation
 *
 * @param {Object} eclDepartureData - Starting point ECL data for the analysis period
 * @param {Object} eclArrivalData - Ending point ECL data for the analysis period
 * @param {Object} eclVariationData - Total change in ECL over the analysis period
 */
const CarouselSection = ({
  eclDepartureData,
  eclArrivalData,
  eclVariationData,
}) => {
  return (
    <>
      {/* Carousel for ECL departure/arrival data - alternates between starting point and ending point */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel
            variant="dark"
            interval={3000} // Auto-rotate every 3 seconds
            controls={true} // Show navigation controls
            indicators={false} // Hide slide indicators
          >
            <Carousel.Item>
              <EmptyCard chartData={eclDepartureData} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard badgeColor="info" chartData={eclArrivalData} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>

      {/* Static card showing the total ECL variation between departure and arrival points */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <EmptyCardVariation eclData={eclVariationData} />
        </PyramidCard>
      </Col>
    </>
  );
};

/**
 * VariationAnalysisCharts Component
 *
 * Main component for displaying ECL variation analysis.
 * Analyzes the changes in Expected Credit Loss (ECL) between two time periods
 * and breaks down those changes by various factors (rating, time, LGD, PD, etc.)
 *
 * This component serves as a comprehensive tool for understanding the drivers
 * behind changes in credit risk provisions.
 */
const VariationAnalysisCharts = () => {
  const { account, companyList, accessToken, userList } = useAuth();
  // Palette for client-specific chart colors
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);

  /**
   * State management for chart data
   * Stores all visualization data categories needed for the various charts
   */
  const [chartData, setChartData] = useState({
    // Summary ECL values
    eclDepartureData: null, // ECL at the start of analysis period
    eclArrivalData: null, // ECL at the end of analysis period
    eclVariationData: null, // Total change in ECL (arrival - departure)

    // Breakdown analysis by various dimensions
    bucketData: [], // IFRS9 stage/bucket breakdown
    sectorData: [], // Industry sector breakdown
    ratingData: [], // Credit rating breakdown
    countriesData: [], // Geographic breakdown with bucket details

    // Waterfall and effects analysis
    waterfallData: [], // Sequential breakdown of ECL change factors
    marginalData: [], // Marginal vs cumulative effects

    // Top contributors by factor type
    topRatingData: [], // Top changes due to rating migrations
    topPdData: [], // Top changes due to PD (Probability of Default) shifts
    topLgdData: [], // Top changes due to LGD (Loss Given Default) shifts
    topTimeData: [], // Top changes due to time value of money
    topPurchaseData: [], // Top changes due to new purchases/disposals
    topIssuerData: [], // Top issuers contributing to changes

    // Distribution analysis
    ratingEffectDistributionData: [], // Distribution of rating migration effects
    bucketEffectDistributionData: [], // Distribution of bucket migration effects
    timeEffectDistributionData: [], // Distribution of time effects by maturity
  });

  /**
   * Effect to update the color palette when the user account changes
   * Maintains consistent branding per client
   */
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  // State for managing the available variation analyses and user selection
  const [variationAnalyses, setVariationAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // API service
  const api = osirisApi();

  // Grouping variation analyses by user
  const groupedVariationAnalyses = useGroupedItems(
    variationAnalyses,
    userList,
    companyList
  );

  /**
   * Effect to fetch available variation analyses when component mounts
   * Retrieves only completed analyses and sorts by creation date
   */
  useEffect(() => {
    const fetchVariationAnalyses = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getVariations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const analyses = Array.isArray(data) ? data : data.result || [];

          const sortedAnalyses = [...analyses].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setVariationAnalyses(sortedAnalyses);

          // Auto-select the most recent analysis
          if (sortedAnalyses.length > 0) {
            setSelectedAnalysis(sortedAnalyses[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVariationAnalyses();
  }, [accessToken]);

  /**
   * Effect to fetch chart data when the selected analysis changes
   * Retrieves and transforms all the data needed for the various visualizations
   */
  useEffect(() => {
    const fetchVariationCharts = async () => {
      if (!selectedAnalysis || !accessToken) return;

      try {
        // Fetch variation charts data for selected analysis
        const data = await api.getVariationCharts(selectedAnalysis);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          // Transform API data into component-friendly format for each chart type
          const transformedData = {
            // Summary ECL values
            eclDepartureData:
              result.find((chart) => chart.Name === "SumEclDeparture")
                ?.Data[0] || null,

            eclArrivalData:
              result.find((chart) => chart.Name === "SumEclArrival")?.Data[0] ||
              null,

            eclVariationData:
              result.find((chart) => chart.Name === "SumEclVariation")
                ?.Data[0] || null,

            // IFRS9 bucket/stage breakdown
            bucketData:
              result.find((chart) => chart.Name === "BreakdownByBucket")
                ?.Data || [],

            // Industry sector breakdown
            sectorData:
              result.find((chart) => chart.Name === "BreakdownBySector")
                ?.Data || [],

            // Credit rating breakdown
            ratingData:
              result.find((chart) => chart.Name === "BreakdownByRating")
                ?.Data || [],

            // Country breakdown with bucket details
            countriesData:
              result.find((chart) => chart.Name === "CountriesWithBucket")
                ?.Data || [],

            // Waterfall chart showing sequential impact of different factors
            waterfallData:
              result.find((chart) => chart.Name === "WaterfallEffects")?.Data ||
              [],

            // Marginal vs. cumulative effect analysis
            marginalData:
              result.find((chart) => chart.Name === "MarginalCumulativeEffect")
                ?.Data || [],

            // Top contributors to ECL change by factor type:

            // Rating migration effects
            topRatingData:
              result.find((chart) => chart.Name === "TopRatingEffect")?.Data ||
              [],

            // Probability of Default effects
            topPdData:
              result.find((chart) => chart.Name === "TopPdEffect")?.Data || [],

            // Loss Given Default effects
            topLgdData:
              result.find((chart) => chart.Name === "TopLgdEffect")?.Data || [],

            // Time value of money effects
            topTimeData:
              result.find((chart) => chart.Name === "TopTimeEffect")?.Data ||
              [],

            // New asset purchase/disposal effects
            topPurchaseData:
              result.find((chart) => chart.Name === "TopPurchaseEffect")
                ?.Data || [],

            // Top issuers contributing to changes
            topIssuerData:
              result.find((chart) => chart.Name === "TopIssuer")?.Data || [],

            // Distribution analyses:

            // Distribution of rating migration effects
            ratingEffectDistributionData:
              result.find((chart) => chart.Name === "RatingEffectDistribution")
                ?.Data || [],

            // Distribution of bucket/stage migration effects
            bucketEffectDistributionData:
              result.find((chart) => chart.Name === "BucketEffectDistribution")
                ?.Data || [],

            // Distribution of time effects by maturity
            timeEffectDistributionData:
              result.find(
                (chart) => chart.Name === "TimeEffectDistributionPerMaturity"
              )?.Data || [],
          };

          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      }
    };

    fetchVariationCharts();
  }, [selectedAnalysis, accessToken]);

  /**
   * Component render - Comprehensive variation analysis dashboard
   * Organized in sections from summary to detailed breakdowns
   */
  return (
    <div>
      {/* Header section with analysis selector and summary cards */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Choose your variation
              </div>
              <SelectBox
                dataSource={groupedVariationAnalyses}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedAnalysis}
                onValueChanged={(e) => setSelectedAnalysis(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>

        {/* Summary ECL cards - departure, arrival, and variation */}
        <CarouselSection
          eclDepartureData={chartData.eclDepartureData}
          eclArrivalData={chartData.eclArrivalData}
          eclVariationData={chartData.eclVariationData}
        />
      </Row>

      {/* Main breakdown section - high-level segmentation */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <BreakdownByBucket
            data={chartData.bucketData}
            palette={currentPalette}
          />
        </Col>
        <Col md={6}>
          <BreakdownBySector
            data={chartData.sectorData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Credit rating breakdown - full width */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <BreakdownByRating
            data={chartData.ratingData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Geographic breakdown - full width */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <CountriesWithBucket
            data={chartData.countriesData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Waterfall analysis - shows sequential impact of factors */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <WaterfallEffects
            data={chartData.waterfallData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Marginal vs. cumulative effects analysis */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <MarginalCumulativeEffects data={chartData.marginalData} />
        </Col>
      </Row>

      {/* Top contributors section - detailed tables by factor type */}

      {/* Rating migration effects */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopRatingEffects data={chartData.topRatingData} />
        </Col>
      </Row>

      {/* Probability of Default effects */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopPdEffects data={chartData.topPdData} />
        </Col>
      </Row>

      {/* Loss Given Default effects */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopLgdEffects data={chartData.topLgdData} />
        </Col>
      </Row>

      {/* Time value of money effects */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopTimeEffects data={chartData.topTimeData} />
        </Col>
      </Row>

      {/* New purchase/disposal effects */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopPurchaseEffects data={chartData.topPurchaseData} />
        </Col>
      </Row>

      {/* Top issuers contributing to changes */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopIssuerEffects data={chartData.topIssuerData} />
        </Col>
      </Row>

      {/* Distribution analysis section - statistical distribution of effects */}

      {/* Rating migration effect distribution */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <RatingEffectDistribution
            data={chartData.ratingEffectDistributionData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Bucket/stage migration effect distribution */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <BucketEffectDistribution
            data={chartData.bucketEffectDistributionData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Time effect distribution by maturity */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TimeEffectDistribution
            data={chartData.timeEffectDistributionData}
            palette={currentPalette}
          />
        </Col>
      </Row>
    </div>
  );
};

export default VariationAnalysisCharts;
